import {ArrowUpDownIcon, CloseIcon} from '@chakra-ui/icons';
import {
  Box,
  BoxProps,
  Button,
  Circle,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Switch,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {HitItemCatalogue} from 'api-types/ffconnect';
import {ArticlesCadencierSuggestionsAjoutesEventDesc} from 'api-types/ffconnect-hal';
import {State} from '@synako/halitrek';
import React, {useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {Results} from '@orama/orama';
import {FromageFilterContent} from '@/components/FromageFilter/FromageFilterContent';
import {
  buildFromageFacets,
  countFromageSelections,
  hasFromageFacets,
} from '@/components/FromageFilter/fromage-utils';
import {ColdIcon} from '@/components/svg/ColdIcon';
import {FilterIcon} from '@/components/svg/FilterIcon';
import {SortType} from '@/domains/ligneProduit/TriArticlesCadencier';
import {TriSectionsCadencier} from '@/domains/ligneProduit/TriSectionsCadencier';
import {SuggestionActions} from '@/domains/ligneProduit/SuggestionActions';
import {FlexProps} from '@chakra-ui/layout';
import {Navigable} from '@synako/halitrek';
import {ClientDesc} from 'api-types/ffconnect-hal';
import {useHalState} from '@synako/haligator';
import {useSearchClientProviderState} from 'ff-common';
import {bottomNavHeight} from '../Navbar/BottomNav/BottomNav';
import {useHorizontalScroll} from '@/hooks/useHorizontalScroll';
import {ChevronLeftIcon} from '@/components/svg/ChevronLeftIcon';
import {ChevronRightBlueIcon} from '@/components/svg/ChevronRightBlueIcon';
import {FilterBarBlueCheckbox} from './FilterBarBlueCheckbox';
import {FilterBarPromoCheckbox} from './FilterBarPromoCheckbox';
import {FilterBarPopoverFilter} from './FilterBarPopoverFilter';
import {FilterBarTag} from './FilterBarTag';
import {FILTER_BUTTON_PROPS} from './FilterBarStyles';

export interface FilterBarFilters {
  nature_du_prix?: string;
  labels?: string[];
  famille_fromage?: string[];
  type_de_lait?: string[];
  traitement_thermique_du_lait?: string[];
  temperature_livraison?: string;
  produit_marche?: boolean;
  afficher_marketplace?: boolean;
  sortBy?: string;
}

export interface FilterBarProps {
  itemResults?: Results<HitItemCatalogue>;
  filters: FilterBarFilters;
  onFilterChange: <K extends keyof FilterBarFilters>(
    key: K,
    value: FilterBarFilters[K],
  ) => void;
  onRemoveAllFilters: (preservePromo: boolean) => void;

  // Filtres à afficher (tous false par défaut sauf promo/labels/fromage/temperature)
  showProduitMarche?: boolean;
  /** Place le filtre Produits marché en tête de la barre (ex. side panel rupture). */
  produitMarcheEnPremier?: boolean;
  showMarketplace?: boolean;
  showTriCatalogue?: boolean;
  showTriCadencier?: boolean;
  showSuggestionActions?: boolean;

  // Props pour filtres spécifiques catalogue
  clientNav?: Navigable<ClientDesc>;
  direction?: FlexProps['direction'];
  containerProps?: BoxProps;

  // Props pour le tri cadencier
  sortType?: SortType;
  onSortTypeChange?: (val: SortType) => void;

  // Props pour les suggestions cadencier
  onSuggestionSuccess?: (
    event: State<ArticlesCadencierSuggestionsAjoutesEventDesc> | null,
  ) => void;

  /**
   * Contenu à gauche de la rangée filtres (desktop + mobile).
   * `variant` : instance distincte par breakpoint.
   * `onExpandedChange` : mobile — Promo/Filtrer en icônes seules.
   */
  leadingContent?: (opts: {
    variant: 'desktop' | 'mobile';
    onExpandedChange: (expanded: boolean) => void;
  }) => React.ReactNode;
}

/*
  Barre de filtres unifiée catalogue + cadencier.
  Filtres communs : Promotions, Labels, Caractéristiques fromage, Non surgelé/Surgelé
  Filtres spécifiques activables via props boolean.
*/
export const FilterBar: React.FC<FilterBarProps> = ({
  itemResults,
  filters,
  onFilterChange,
  onRemoveAllFilters,
  showProduitMarche = false,
  produitMarcheEnPremier = false,
  showMarketplace = false,
  showTriCatalogue = false,
  showTriCadencier = false,
  showSuggestionActions = false,
  clientNav,
  direction,
  containerProps,
  sortType,
  onSortTypeChange,
  onSuggestionSuccess,
  leadingContent,
}) => {
  const {t} = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const {canScrollLeft, canScrollRight, scrollToStart, scrollToEnd} =
    useHorizontalScroll(scrollRef);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  // Recherche mobile déployée : Promo / Filtrer passent en icônes seules.
  const [leadingExpanded, setLeadingExpanded] = useState(false);
  const compactMobileActions = leadingExpanded;
  const {marketplaceReady: hasMarketplace} = useSearchClientProviderState();
  const location = useLocation();
  // Si l'utilisateur arrive depuis un lien promo, on désactive la checkbox promo
  const fromPromo =
    new URLSearchParams(location.search).get('from') === 'promo';

  // Facettes Orama
  const promoCount =
    itemResults?.facets?.['nature_du_prix']?.values['promo'] ?? 0;
  const labels = useMemo(
    () =>
      Object.entries(itemResults?.facets?.['labels']?.values ?? {}).filter(
        ([key]) => key !== '',
      ),
    [itemResults],
  );
  const labelsCount = labels.length;
  const fromageFacets = useMemo(
    () => buildFromageFacets(itemResults?.facets ?? {}),
    [itemResults],
  );
  const temperatureFacets =
    itemResults?.facets?.['temperature_livraison']?.values ?? {};
  const fraisCount = temperatureFacets['positif'] ?? 0;
  const surgeleCount = temperatureFacets['negatif'] ?? 0;
  const produitMarcheValues = itemResults?.facets?.['produit_marche']?.values;
  const produitMarcheCount = produitMarcheValues
    ? (produitMarcheValues['true'] ?? 0)
    : 0;

  const selectedLabels = filters.labels ?? [];
  const selectedFamilleFromage = filters.famille_fromage ?? [];
  const selectedTypeDeLait = filters.type_de_lait ?? [];
  const selectedTraitementDuLait = filters.traitement_thermique_du_lait ?? [];
  const fromageSelectionCount = countFromageSelections(
    selectedFamilleFromage,
    selectedTypeDeLait,
    selectedTraitementDuLait,
  );

  const filtrePromo = filters.nature_du_prix === 'promo';
  const filtreSurgele = filters.temperature_livraison === 'negatif';
  const hasPromo = promoCount > 0;
  const hasLabels = labelsCount > 0;
  const hasTemperature = fraisCount > 0 || surgeleCount > 0;
  const hasProduitMarche = produitMarcheCount > 0;
  // Filtre Produits marché : seulement si la fiche client l'active et que les résultats en contiennent,
  // ou s'il est coché — une recherche sans produit marché ne doit pas retirer la case qui permet de le décocher.
  const clientState = useHalState(showProduitMarche ? clientNav : undefined);
  const afficherFiltreProduitMarche =
    clientState.data?.affichage_produit_marche === 'active' &&
    (hasProduitMarche || filters.produit_marche === true);

  // "Tout effacer" n'apparaît que si des filtres avec tags sont actifs (pas promos/produit marché seuls)
  const hasActiveFilters =
    selectedLabels.length > 0 ||
    !!filters.temperature_livraison ||
    fromageSelectionCount > 0;
  // Pastille du bouton « Filtrer » (mobile) : Produits marché n'est visible que dans le drawer,
  // la pastille doit donc aussi le signaler quand il est coché.
  const pastilleFiltrer = hasActiveFilters || filters.produit_marche === true;

  // Ajoute ou retire un label de la sélection, reset à undefined si aucun label sélectionné
  const handleLabelChange = (label: string, checked: boolean) => {
    const newLabels = checked
      ? [...selectedLabels, label]
      : selectedLabels.filter((l) => l !== label);
    onFilterChange('labels', newLabels.length > 0 ? newLabels : undefined);
  };

  // Contenu des checkboxes labels, réutilisé dans le popover desktop et le drawer mobile
  const labelsCheckboxes = useMemo(
    () => (
      <Flex
        direction='column'
        gap={2}
      >
        {labels.map(([label, count]) =>
          count ? (
            <FilterBarBlueCheckbox
              key={label}
              isChecked={selectedLabels.includes(label)}
              onChange={(checked) => {
                handleLabelChange(label, checked);
              }}
            >
              {label} ({count})
            </FilterBarBlueCheckbox>
          ) : null,
        )}
      </Flex>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labels, selectedLabels],
  );

  // Contenu des checkboxes température, réutilisé dans le popover desktop et le drawer mobile
  const temperatureCheckboxes = useMemo(
    () => (
      <Flex
        direction='column'
        gap={2}
      >
        {fraisCount > 0 && (
          <FilterBarBlueCheckbox
            isChecked={filters.temperature_livraison === 'positif'}
            onChange={(checked) => {
              onFilterChange(
                'temperature_livraison',
                checked ? 'positif' : undefined,
              );
            }}
          >
            {t('catalogue.filtre.non_surgele')} ({fraisCount})
          </FilterBarBlueCheckbox>
        )}
        {surgeleCount > 0 && (
          <FilterBarBlueCheckbox
            isChecked={filters.temperature_livraison === 'negatif'}
            onChange={(checked) => {
              onFilterChange(
                'temperature_livraison',
                checked ? 'negatif' : undefined,
              );
            }}
          >
            {t('catalogue.filtre.surgele')} ({surgeleCount})
          </FilterBarBlueCheckbox>
        )}
      </Flex>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fraisCount, surgeleCount, filters.temperature_livraison, t],
  );

  // Contenu du filtre fromage, réutilisé dans le popover desktop et le drawer mobile
  const fromageContent = (
    <FromageFilterContent
      facets={fromageFacets}
      selectedFamilleFromage={selectedFamilleFromage}
      selectedTypeDeLait={selectedTypeDeLait}
      selectedTraitementDuLait={selectedTraitementDuLait}
      onFamilleFromageChange={(v) => {
        onFilterChange('famille_fromage', v.length > 0 ? v : undefined);
      }}
      onTypeDeLaitChange={(v) => {
        onFilterChange('type_de_lait', v.length > 0 ? v : undefined);
      }}
      onTraitementDuLaitChange={(v) => {
        onFilterChange(
          'traitement_thermique_du_lait',
          v.length > 0 ? v : undefined,
        );
      }}
    />
  );

  // "Tout effacer" : délègue au parent qui fait un seul changeMultiple, en lui indiquant
  // s'il faut préserver le filtre promo (lien `?from=promo`). Évite un double fetch Orama.
  const handleRemoveAllFilters = () => {
    onRemoveAllFilters(fromPromo);
  };

  // Bascule entre tri par pertinence et tri alphabétique
  const handleSortChange = () => {
    const newSortBy =
      filters.sortBy === 'alphabetique' ? undefined : 'alphabetique';
    onFilterChange('sortBy', newSortBy);
  };

  // Case Produits marché, réutilisée dans la barre desktop et le drawer mobile
  const produitMarcheCheckbox = (
    <FilterBarBlueCheckbox
      isChecked={filters.produit_marche === true}
      onChange={(checked) => {
        onFilterChange('produit_marche', checked ? true : undefined);
      }}
    >
      {t('catalogue.filtre.produit_marche')}
    </FilterBarBlueCheckbox>
  );

  // Filtre Produits marché — rendu en tête ou après température selon `produitMarcheEnPremier`.
  const filtreProduitMarche = afficherFiltreProduitMarche ? (
    <Box
      height='38px'
      p='7px 11px'
      borderRadius='8px'
      fontWeight='400'
      border='2px'
      borderColor={filters.produit_marche ? 'gray.dark' : 'gray.light'}
      bg='gray.75'
    >
      {produitMarcheCheckbox}
    </Box>
  ) : null;

  // Même filtre dans le drawer mobile, la barre mobile n'affichant que Promotions et Filtrer
  const filtreProduitMarcheDrawer = afficherFiltreProduitMarche ? (
    <Box mb={4}>{produitMarcheCheckbox}</Box>
  ) : null;

  return (
    <Flex
      direction='column'
      w='100%'
      gap={2}
      {...containerProps}
    >
      {/* === VERSION DESKTOP === */}
      <Flex
        w='100%'
        align='center'
        display={{base: 'none', md: 'flex'}}
        direction={direction ?? 'row'}
        gap={2}
      >
        {leadingContent?.({
          variant: 'desktop',
          onExpandedChange: setLeadingExpanded,
        })}
        {/* Conteneur scrollable des filtres */}
        <Box
          flex='1'
          minW={0}
          maxW='100%'
          position='relative'
        >
          {/* Chevron gauche */}
          {canScrollLeft && (
            <IconButton
              aria-label='Défiler vers la gauche'
              icon={<ChevronLeftIcon color='white' />}
              position='absolute'
              left={1}
              top='50%'
              transform='translateY(-50%)'
              zIndex={2}
              bg='gray.dark'
              borderRadius='full'
              h='19px'
              w='19px'
              minW='19px'
              p='0'
              _hover={{bg: 'black.text'}}
              onClick={scrollToStart}
            />
          )}
          {/* Chevron droit */}
          {canScrollRight && (
            <IconButton
              aria-label='Défiler vers la droite'
              icon={<ChevronRightBlueIcon color='white' />}
              position='absolute'
              right={1}
              top='50%'
              transform='translateY(-50%)'
              zIndex={2}
              bg='gray.dark'
              borderRadius='full'
              h='19px'
              w='19px'
              minW='19px'
              p='0'
              _hover={{bg: 'black.text'}}
              onClick={scrollToEnd}
            />
          )}
          <HStack
            ref={scrollRef}
            spacing={2}
            overflowX='auto'
            w='100%'
            py={1}
            css={{
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              '& > *': {
                flexShrink: 0,
              },
            }}
          >
            {/* Produits marché en tête (side panel rupture) */}
            {produitMarcheEnPremier && filtreProduitMarche}

            {/* Promotions */}
            <FilterBarPromoCheckbox
              isChecked={filtrePromo}
              isDisabled={!hasPromo || fromPromo}
              hasPromo={hasPromo}
              onChange={(checked) => {
                onFilterChange('nature_du_prix', checked ? 'promo' : undefined);
              }}
            />

            {/* Labels */}
            <FilterBarPopoverFilter
              label={t('catalogue.filtre.labels')}
              isActive={selectedLabels.length > 0}
              isDisabled={!hasLabels}
            >
              {labelsCheckboxes}
            </FilterBarPopoverFilter>

            {/* Caractéristiques fromage */}
            <FilterBarPopoverFilter
              label={t('catalogue.filtre.fromage')}
              isActive={fromageSelectionCount > 0}
              isDisabled={!hasFromageFacets(fromageFacets)}
              bodyMaxH='400px'
              contentMaxW='700px'
            >
              {fromageContent}
            </FilterBarPopoverFilter>

            {/* Non surgelé / Surgelé */}
            <FilterBarPopoverFilter
              label={t('catalogue.filtre.non_surgele_surgele')}
              isActive={!!filters.temperature_livraison}
              isDisabled={!hasTemperature}
              contentMaxW='300px'
              buttonProps={{
                borderColor: filtreSurgele
                  ? 'cyan.surgele_border'
                  : filters.temperature_livraison
                    ? 'gray.dark'
                    : 'gray.light',
                bg: filtreSurgele ? 'cyan.surgele_bg' : 'gray.75',
                leftIcon: filtreSurgele ? <ColdIcon boxSize={5} /> : undefined,
              }}
            >
              {temperatureCheckboxes}
            </FilterBarPopoverFilter>

            {/* Produit marché — catalogue (après température) */}
            {!produitMarcheEnPremier && filtreProduitMarche}

            {/* Catalogues partenaires — spécifique catalogue */}
            {showMarketplace && hasMarketplace && (
              <HStack
                height='38px'
                p='7px 11px'
                borderRadius='8px'
                bg='gray.75'
                border='2px'
                borderColor={
                  filters.afficher_marketplace === false
                    ? 'gray.dark'
                    : 'gray.light'
                }
                spacing={2}
              >
                <Switch
                  size='sm'
                  isChecked={filters.afficher_marketplace !== false}
                  onChange={(e) => {
                    onFilterChange(
                      'afficher_marketplace',
                      e.target.checked ? undefined : false,
                    );
                  }}
                  colorScheme='blue'
                  sx={{
                    '& .chakra-switch__track': {
                      bg:
                        filters.afficher_marketplace !== false
                          ? 'blue.main'
                          : 'gray.300',
                    },
                  }}
                />
                <Text
                  fontSize='xs'
                  fontWeight='500'
                  color='blue.main'
                  whiteSpace='nowrap'
                >
                  {t('catalogue.filtre.afficher_marketplace')}
                </Text>
              </HStack>
            )}

            {/* Spacer flexible : pousse tri/suggestions à droite s'il y a de la place, disparaît sinon */}
            <Box flex='1 0 0px' />

            {/* Tri catalogue */}
            {showTriCatalogue && (
              <HStack
                bg='gray.75'
                border='2px solid'
                borderColor='gray.light'
                borderRadius='8px'
                h='38px'
                w='140px'
                px={2}
                alignItems='center'
                justifyContent='space-between'
                cursor='pointer'
                onClick={handleSortChange}
              >
                <Text
                  color='blue.text'
                  fontWeight='700'
                  fontSize='xs'
                  whiteSpace='nowrap'
                >
                  {t('cadencier.tri.tri')}
                </Text>
                <Text
                  color='blue.text'
                  fontWeight='500'
                  fontSize='xs'
                  whiteSpace='nowrap'
                >
                  {filters.sortBy === 'alphabetique'
                    ? t('catalogue.filtre.a-z')
                    : t('catalogue.filtre.pertinence')}
                </Text>
                <ArrowUpDownIcon
                  color='blue.main'
                  boxSize='18px'
                />
              </HStack>
            )}

            {/* Tri cadencier */}
            {showTriCadencier && sortType && onSortTypeChange && (
              <TriSectionsCadencier
                value={sortType}
                onChange={onSortTypeChange}
              />
            )}

            {/* Suggestions cadencier */}
            {showSuggestionActions && (
              <SuggestionActions onSuggestionSuccess={onSuggestionSuccess} />
            )}
          </HStack>
        </Box>
      </Flex>

      {/* Tags filtres actifs — desktop */}
      {hasActiveFilters && (
        <Flex
          w='100%'
          display={{base: 'none', md: 'flex'}}
        >
          <HStack
            spacing={2}
            alignItems='center'
          >
            <Wrap spacing={2}>
              {selectedLabels.map((label) => (
                <WrapItem key={label}>
                  <FilterBarTag
                    label={label}
                    onRemove={() => {
                      handleLabelChange(label, false);
                    }}
                  />
                </WrapItem>
              ))}
              {selectedFamilleFromage.map((code) => (
                <WrapItem key={`ff-${code}`}>
                  <FilterBarTag
                    label={t(`fromage.famille.${code}`)}
                    onRemove={() => {
                      onFilterChange(
                        'famille_fromage',
                        selectedFamilleFromage.filter((c) => c !== code),
                      );
                    }}
                  />
                </WrapItem>
              ))}
              {selectedTypeDeLait.map((code) => (
                <WrapItem key={`tl-${code}`}>
                  <FilterBarTag
                    label={t(`fromage.lait.${code}`)}
                    onRemove={() => {
                      onFilterChange(
                        'type_de_lait',
                        selectedTypeDeLait.filter((c) => c !== code),
                      );
                    }}
                  />
                </WrapItem>
              ))}
              {selectedTraitementDuLait.map((code) => (
                <WrapItem key={`tt-${code}`}>
                  <FilterBarTag
                    label={t(`fromage.traitement.${code}`)}
                    onRemove={() => {
                      onFilterChange(
                        'traitement_thermique_du_lait',
                        selectedTraitementDuLait.filter((c) => c !== code),
                      );
                    }}
                  />
                </WrapItem>
              ))}
              {filters.temperature_livraison && (
                <WrapItem>
                  <FilterBarTag
                    label={
                      filters.temperature_livraison === 'positif'
                        ? t('catalogue.filtre.non_surgele')
                        : t('catalogue.filtre.surgele')
                    }
                    onRemove={() => {
                      onFilterChange('temperature_livraison', undefined);
                    }}
                  />
                </WrapItem>
              )}
            </Wrap>
            <Box>
              <Tooltip
                label={t('catalogue.filtre.effacer')}
                hasArrow
              >
                <Button
                  variant='unstyled'
                  px={2}
                  bg='transparent'
                  onClick={handleRemoveAllFilters}
                  display='flex'
                  alignItems='center'
                >
                  <Text
                    fontSize='small'
                    color='gray.500'
                    textDecoration='underline'
                    _hover={{textDecoration: 'none'}}
                    transition='all 0.2s'
                  >
                    {t('catalogue.filtre.tout_effacer')}
                  </Text>
                </Button>
              </Tooltip>
            </Box>
          </HStack>
        </Flex>
      )}

      {/* === VERSION MOBILE : loupe (+ Promo + Filtrer) === */}
      <HStack
        spacing={2}
        display={{base: 'flex', md: 'none'}}
        w='100%'
        mt='10px'
        align='center'
      >
        {leadingContent?.({
          variant: 'mobile',
          onExpandedChange: setLeadingExpanded,
        })}
        {/* Promo checkbox */}
        <FilterBarPromoCheckbox
          mobile
          count={promoCount}
          isChecked={filtrePromo}
          isDisabled={!hasPromo || fromPromo}
          hasPromo={hasPromo}
          flex={compactMobileActions ? '0 0 auto' : undefined}
          iconsOnly={compactMobileActions}
          onChange={(checked) => {
            onFilterChange('nature_du_prix', checked ? 'promo' : undefined);
          }}
        />

        {/* Bouton Filtrer */}
        <Box
          as='button'
          type='button'
          height={FILTER_BUTTON_PROPS.height}
          fontSize='sm'
          fontWeight='400'
          color='gray.dark'
          px={compactMobileActions ? '8px' : '11px'}
          py='7px'
          borderRadius='8px'
          bg='gray.75'
          border='1px'
          borderColor='gray.light'
          flex={compactMobileActions ? '0 0 auto' : 1}
          minW={compactMobileActions ? FILTER_BUTTON_PROPS.height : 0}
          w={compactMobileActions ? FILTER_BUTTON_PROPS.height : undefined}
          display='flex'
          alignItems='center'
          justifyContent='center'
          transition='width 0.2s ease, min-width 0.2s ease, padding 0.2s ease, flex 0.2s ease'
          onClick={() => {
            setIsFilterDrawerOpen(true);
          }}
          cursor='pointer'
          aria-label={t('catalogue.filtre.filtrer')}
          position='relative'
        >
          {compactMobileActions ? (
            <>
              <FilterIcon />
              {pastilleFiltrer && (
                <Circle
                  size='8px'
                  bg='blue.main'
                  position='absolute'
                  top='4px'
                  right='4px'
                />
              )}
            </>
          ) : (
            <HStack spacing={2}>
              <Text>{t('catalogue.filtre.filtrer')}</Text>
              <FilterIcon />
              {pastilleFiltrer && (
                <Circle
                  size='15px'
                  bg='blue.main'
                />
              )}
            </HStack>
          )}
        </Box>
      </HStack>

      {/* Suggestion actions mobile — cadencier */}
      {showSuggestionActions && (
        <Box
          display={{base: 'block', md: 'none'}}
          w='100%'
          mb={{base: 2, md: 0}}
        >
          <SuggestionActions onSuggestionSuccess={onSuggestionSuccess} />
        </Box>
      )}

      {/* Drawer mobile — tous les filtres */}
      <Drawer
        placement='bottom'
        isOpen={isFilterDrawerOpen}
        onClose={() => {
          setIsFilterDrawerOpen(false);
        }}
      >
        <DrawerOverlay />
        <DrawerContent
          borderTopRadius='16px'
          bg='white'
          maxH='80vh'
        >
          <DrawerHeader borderBottomWidth='1px'>
            <HStack justifyContent='space-between'>
              <HStack>
                <Text fontWeight='700'>{t('catalogue.filtre.filtrer')}</Text>
                {hasActiveFilters && (
                  <Button
                    variant='unstyled'
                    ml='30px'
                    fontSize='sm'
                    color='gray.dark'
                    textDecoration='underline'
                    onClick={() => {
                      handleRemoveAllFilters();
                      setIsFilterDrawerOpen(false);
                    }}
                  >
                    {t('catalogue.filtre.tout_effacer')}
                  </Button>
                )}
              </HStack>
              <IconButton
                aria-label='Fermer'
                icon={<CloseIcon />}
                size='sm'
                variant='ghost'
                onClick={() => {
                  setIsFilterDrawerOpen(false);
                }}
              />
            </HStack>
          </DrawerHeader>
          <DrawerBody
            pb={bottomNavHeight}
            overflowY='auto'
          >
            {/* Produits marché en tête (side panel rupture) */}
            {produitMarcheEnPremier && filtreProduitMarcheDrawer}
            {/* Labels */}
            {hasLabels && (
              <Box mb={4}>
                <Text
                  fontSize='sm'
                  fontWeight='700'
                  mb={2}
                >
                  {t('catalogue.filtre.labels')} ({labelsCount})
                </Text>
                {labelsCheckboxes}
              </Box>
            )}
            {/* Fromage */}
            {hasFromageFacets(fromageFacets) && (
              <Box mb={4}>
                <Text
                  fontSize='sm'
                  fontWeight='700'
                  mb={2}
                >
                  {t('catalogue.filtre.fromage')}
                </Text>
                {fromageContent}
              </Box>
            )}
            {/* Température */}
            {hasTemperature && (
              <Box mb={4}>
                <Text
                  fontSize='sm'
                  fontWeight='700'
                  mb={2}
                >
                  {t('catalogue.filtre.non_surgele_surgele')}
                </Text>
                {temperatureCheckboxes}
              </Box>
            )}
            {/* Produit marché — catalogue (après température) */}
            {!produitMarcheEnPremier && filtreProduitMarcheDrawer}
            {/* Tri catalogue */}
            {showTriCatalogue && (
              <Box mb={4}>
                <Text
                  fontSize='sm'
                  fontWeight='700'
                  mb={2}
                >
                  {t('catalogue.filtre.trier_par')}
                </Text>
                <HStack spacing={4}>
                  <Button
                    variant={
                      filters.sortBy !== 'alphabetique' ? 'solid' : 'ghost'
                    }
                    colorScheme={
                      filters.sortBy !== 'alphabetique' ? 'blue' : 'gray'
                    }
                    size='xs'
                    onClick={handleSortChange}
                  >
                    {t('catalogue.filtre.pertinence')}
                  </Button>
                  <Button
                    variant={
                      filters.sortBy === 'alphabetique' ? 'solid' : 'ghost'
                    }
                    colorScheme={
                      filters.sortBy === 'alphabetique' ? 'blue' : 'gray'
                    }
                    size='xs'
                    onClick={handleSortChange}
                  >
                    {t('catalogue.filtre.a-z')}
                  </Button>
                </HStack>
              </Box>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};
